import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  advanceUser,
  fetchMySites,
  fetchPhaseCatalog,
  fetchSiteProgress,
  setPreferredSite,
  toggleTask,
} from '../api';

const KEY_CATALOG = ['progression', 'catalog'] as const;
const KEY_MY_SITES = ['user-sites', 'me'] as const;
/** Exportée pour invalider la progression après des changements qui déclenchent l’auto-éval API (ex. droits document). */
export const KEY_SITE_PROGRESS = (siteId: string) => ['progression', 'site', siteId] as const;
/** Préfixe pour `invalidateQueries` (tous les sites chargés). */
export const KEY_SITE_PROGRESS_ROOT = ['progression', 'site'] as const;

export const useCatalog = () =>
  useQuery({ queryKey: KEY_CATALOG, queryFn: fetchPhaseCatalog, staleTime: 5 * 60_000 });

export const useMySites = (enabled = true) =>
  useQuery({ queryKey: KEY_MY_SITES, queryFn: fetchMySites, staleTime: 60_000, enabled });

export const useSiteProgress = (siteId: string | null) =>
  useQuery({
    queryKey: siteId ? KEY_SITE_PROGRESS(siteId) : ['progression', 'site', '__none__'],
    queryFn: () => fetchSiteProgress(siteId as string),
    enabled: Boolean(siteId),
    /** Hors du cache 30s global : au retour sur le Parcours il faut relancer l’API (auto-éval côté serveur). */
    staleTime: 0,
    refetchOnMount: 'always',
  });

export function useToggleTask(siteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskCode, completed }: { taskCode: string; completed: boolean }) =>
      toggleTask(siteId, taskCode, { completed }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY_SITE_PROGRESS(siteId) });
      void qc.invalidateQueries({ queryKey: KEY_MY_SITES });
    },
  });
}

export function useAdvanceUser(siteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => advanceUser(siteId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY_SITE_PROGRESS(siteId) });
      void qc.invalidateQueries({ queryKey: KEY_MY_SITES });
    },
  });
}

export function useSetPreferredSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (siteId: string) => setPreferredSite(siteId),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY_MY_SITES }),
  });
}
